import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";

import { api } from "../api/client";
import {
  CalendarDays,
  CircleHelp,
  Clock,
  Ellipsis,
  FileText,
  GraduationCap,
  House,
  LayoutDashboard,
  LogOut,
  MessagesSquare,
  Network,
  Palmtree,
  Receipt,
  Target,
  UserRound,
  Users,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import SignOutConfirm from "./SignOutConfirm";

type NavItem = {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
  /** Only shown to people with team/leave-approval permissions. */
  managerOnly?: boolean;
};

const MAIN_ITEMS: NavItem[] = [
  { to: "/home", labelKey: "nav.home", icon: House, end: true },
  { to: "/clock", labelKey: "nav.clock", icon: Clock, end: true },
  { to: "/leave", labelKey: "nav.leave", icon: Palmtree, end: false },
];

/* The fourth slot in the bar: My Team for anyone who manages people (their
 * Performance moves to the More menu); otherwise Training while that feature
 * is on, otherwise Performance (which then leaves the More menu). */
const TRAINING_ITEM: NavItem = { to: "/training", labelKey: "nav.training", icon: GraduationCap };
const PERFORMANCE_ITEM: NavItem = { to: "/performance", labelKey: "nav.performance", icon: Target };
const MY_TEAM_ITEM: NavItem = { to: "/team", labelKey: "nav.myTeam", icon: UsersRound };

const MORE_ITEMS: NavItem[] = [
  { to: "/team", labelKey: "nav.myTeam", icon: UsersRound, managerOnly: true },
  { to: "/training", labelKey: "nav.training", icon: GraduationCap },
  { to: "/social", labelKey: "nav.social", icon: MessagesSquare },
  { to: "/calendar", labelKey: "nav.calendar", icon: CalendarDays },
  { to: "/documents", labelKey: "nav.documents", icon: FileText },
  { to: "/expenses", labelKey: "nav.expenses", icon: Receipt },
  { to: "/performance", labelKey: "nav.performance", icon: Target },
  { to: "/org-chart", labelKey: "nav.orgChart", icon: Network },
  { to: "/directory", labelKey: "nav.directory", icon: Users },
  { to: "/help", labelKey: "nav.help", icon: CircleHelp },
  { to: "/me", labelKey: "nav.profile", icon: UserRound },
];

/* Map MORE_ITEMS paths to the feature flag that gates them */
const PATH_FEATURE_MAP: Record<string, string> = {
  "/social": "social_feed_enabled",
  "/expenses": "expenses_enabled",
  "/review": "360_reviews_enabled",
  "/training": "training_enabled",
  "/org-chart": "org_chart_enabled",
};

const ICON_PROPS = { size: 22, strokeWidth: 1.75, "aria-hidden": true } as const;

export default function BottomNav() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { employee, isAdmin, viewAsLevel, hasPermission } = useAuth();
  const { isEnabled } = useFeatures();
  const [moreOpen, setMoreOpen] = useState(false);
  const [showSignOut, setShowSignOut] = useState(false);

  /* Live presence: a soft green dot on the Check in tab while checked in
   * (amber on a break). Kept fresh by the check-in screen via a window
   * event, with one quiet fetch as the starting value. */
  const [clockState, setClockState] = useState<string>("not_clocked_in");
  useEffect(() => {
    let on = true;
    api
      .get<{ status: string }>("/api/clock/status")
      .then((r) => {
        if (on && r?.status) setClockState(r.status);
      })
      .catch(() => {});
    const onStatus = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (detail) setClockState(detail);
    };
    window.addEventListener("pme-clock-status", onStatus);
    return () => {
      on = false;
      window.removeEventListener("pme-clock-status", onStatus);
    };
  }, []);

  const isManagerLike =
    isAdmin || hasPermission("can_view_team") || hasPermission("can_approve_leave");

  const trainingOn = isEnabled("training_enabled");
  const mainItems = useMemo(
    () => [
      ...MAIN_ITEMS,
      isManagerLike ? MY_TEAM_ITEM : trainingOn ? TRAINING_ITEM : PERFORMANCE_ITEM,
    ],
    [isManagerLike, trainingOn],
  );

  const filteredMoreItems = useMemo(
    () =>
      MORE_ITEMS.filter((item) => {
        if (item.managerOnly && !isManagerLike) return false;
        if (mainItems.some((m) => m.to === item.to)) return false;
        const flag = PATH_FEATURE_MAP[item.to];
        return !flag || isEnabled(flag);
      }),
    [isEnabled, isManagerLike, mainItems],
  );

  return (
    <>
      {showSignOut && <SignOutConfirm onClose={() => setShowSignOut(false)} />}
      {moreOpen && (
        <div
          className="fixed inset-0 z-20 bg-pme-navy/40 backdrop-blur-[2px]"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="absolute bottom-[61px] left-0 right-0 mx-auto max-w-xl rounded-t-3xl bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-3 gap-2">
              {filteredMoreItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMoreOpen(false)}
                    className="flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-2xl py-2 text-xs text-pme-ink hover:bg-pme-sky/50"
                  >
                    <Icon {...ICON_PROPS} className="text-pme-blue" />
                    <span className="font-medium">{t(item.labelKey)}</span>
                  </Link>
                );
              })}
              {isAdmin && !(viewAsLevel && !viewAsLevel.is_admin) && (
                <Link
                  to="/admin"
                  onClick={() => setMoreOpen(false)}
                  className="flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-2xl bg-pme-navy py-2 text-xs text-white hover:bg-pme-navy/90"
                >
                  <LayoutDashboard {...ICON_PROPS} />
                  <span className="font-medium">{t("nav.admin")}</span>
                </Link>
              )}
            </div>
            {employee && (
              <>
                <div className="my-3 border-t border-pme-line" />
                <button
                  type="button"
                  onClick={() => { setMoreOpen(false); setShowSignOut(true); }}
                  className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  <LogOut size={18} strokeWidth={1.75} aria-hidden />
                  {t("auth.logout")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <nav
        aria-label="Primary"
        className="safe-bottom safe-x fixed inset-x-0 bottom-0 z-10 border-t border-pme-line bg-white/95 backdrop-blur"
      >
        <ul className="mx-auto flex max-w-xl">
          {mainItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to} className="flex-1">
                <NavLink
                  to={item.to}
                  end={item.end ?? false}
                  className={({ isActive }) =>
                    [
                      "flex min-h-[60px] flex-col items-center justify-center gap-1 py-2 text-[11px]",
                      // A review or feedback form (/review) belongs to Performance.
                      isActive || (item.to === "/performance" && pathname === "/review")
                        ? "text-pme-blue"
                        : "text-gray-400 hover:text-pme-navy",
                    ].join(" ")
                  }
                >
                  <span className="relative">
                    <Icon {...ICON_PROPS} />
                    {item.to === "/clock" && clockState !== "not_clocked_in" && (
                      <span
                        aria-hidden
                        className={[
                          "live-dot absolute -right-1 -top-0.5 h-2 w-2 rounded-full ring-2 ring-white",
                          clockState === "clocked_in"
                            ? "bg-emerald-500 text-emerald-500"
                            : "bg-amber-500 text-amber-500",
                        ].join(" ")}
                      />
                    )}
                  </span>
                  <span className="font-semibold">{t(item.labelKey)}</span>
                </NavLink>
              </li>
            );
          })}
          <li className="flex-1">
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              aria-expanded={moreOpen}
              className={[
                "flex min-h-[60px] w-full flex-col items-center justify-center gap-1 py-2 text-[11px]",
                moreOpen ? "text-pme-blue" : "text-gray-400 hover:text-pme-navy",
              ].join(" ")}
            >
              <Ellipsis {...ICON_PROPS} />
              <span className="font-semibold">{t("nav.more")}</span>
            </button>
          </li>
        </ul>
      </nav>
    </>
  );
}
